import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Gavel, Search, Sparkles, ShieldAlert, AlertTriangle, FileText,
  Loader2, Building2, Users, Banknote, FlaskConical
} from "lucide-react";
import { toast } from "sonner";
import IdentityResultCard from "../components/IdentityResultCard";
import { fromEnforcement } from "../lib/identityNormalizers";

const API = `${window.location.origin}/api/enforcement`;

const FLAG_COLORS = {
  critical: { bg: "rgba(220, 38, 38, 0.18)", border: "#dc2626", text: "#fca5a5" },
  high:     { bg: "rgba(239, 68, 68, 0.12)", border: "#ef4444", text: "#ef4444" },
  medium:   { bg: "rgba(245, 158, 11, 0.10)", border: "#f59e0b", text: "#f59e0b" },
  low:      { bg: "rgba(34, 197, 94, 0.10)",  border: "#22c55e", text: "#22c55e" },
};

const AGENCY_COLORS = {
  ED:   { bg: "rgba(220, 38, 38, 0.10)", border: "#dc2626", text: "#fca5a5" },
  CBI:  { bg: "rgba(139, 92, 246, 0.12)", border: "#8b5cf6", text: "#c4b5fd" },
  SFIO: { bg: "rgba(245, 158, 11, 0.10)", border: "#f59e0b", text: "#fbbf24" },
};

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "18px" }}>
      <div className="flex items-center justify-between mb-2">
        <div style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px" }}>{label}</div>
        <Icon style={{ width: 16, height: 16, color }} />
      </div>
      <div style={{ color, fontSize: "28px", fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function formatINR(n) {
  if (!n) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toLocaleString("en-IN", { maximumFractionDigits: 1 })} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function AgencyCard({ agency, data, fields }) {
  const c = AGENCY_COLORS[agency];
  const action = data?.action_type || (agency === "SFIO" ? null : "None");
  const isActive = agency === "SFIO"
    ? (data?.companies_under_investigation?.length || 0) > 0 || data?.auditor_debarred
    : (action && action.toLowerCase() !== "none");

  return (
    <div style={{ background: isActive ? c.bg : "#0d1117", border: `1px solid ${isActive ? c.border : "#1e2530"}`, borderRadius: "10px", padding: "16px" }}>
      <div className="flex items-center justify-between mb-3">
        <div style={{ color: c.text, fontSize: "14px", fontWeight: 800, letterSpacing: "1px" }}>{agency}</div>
        <span style={{ background: isActive ? c.border : "#1e2530", color: isActive ? "white" : "#94a3b8", fontSize: "9px", padding: "2px 8px", borderRadius: "3px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {isActive ? "ACTION" : "CLEAR"}
        </span>
      </div>
      {!isActive ? (
        <div style={{ color: "#22c55e", fontSize: "12px" }}>✓ No action on record</div>
      ) : (
        <div style={{ fontSize: "12px", color: "#cbd5e1", lineHeight: 1.6 }}>
          {fields.map((f, idx) => {
            const val = data?.[f.key];
            if (!val || (Array.isArray(val) && !val.length) || (typeof val === "number" && val === 0)) return null;
            return (
              <div key={idx} style={{ marginBottom: "4px" }}>
                <span style={{ color: "#64748b" }}>{f.label}:</span>{" "}
                <strong style={{ color: "#f1f5f9" }}>
                  {f.format ? f.format(val) : (Array.isArray(val) ? val.join(", ") : val)}
                </strong>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function EnforcementPanel({ initialName = null, compact = false }) {
  const [queryName, setQueryName] = useState(initialName || "");
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [risk, setRisk] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, ed: 0, cbi: 0, sfio: 0, cases_created: 0 });

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lookups`, { withCredentials: true });
      setHistory(data.lookups || []);
      setStats(data.stats || { total: 0, ed: 0, cbi: 0, sfio: 0, cases_created: 0 });
    } catch (err) { console.error("[Enforcement] Failed to load history:", err); }
  }, []);

  useEffect(() => { if (!compact) loadHistory(); }, [loadHistory, compact]);

  const runLookup = async () => {
    if (!queryName.trim()) { toast.error("Enter a name"); return; }
    setLooking(true); setResult(null); setRisk(null);
    try {
      const { data } = await axios.post(
        `${API}/lookup`,
        { query_name: queryName },
        { withCredentials: true }
      );
      setResult(data.result);
      setRisk(data.risk);
      const flagCount = data.risk?.flags?.length || 0;
      toast.success(flagCount > 0 ? `${flagCount} enforcement flag(s) — ${data.risk.severity_level.toUpperCase()}` : "Clear — no enforcement actions");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally {
      setLooking(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/save`,
        { query_name: queryName, result, link_to_customer_id: null },
        { withCredentials: true }
      );
      if (data.created_case) {
        toast.success(`Saved. Case ${data.created_case.case_id} auto-created.`);
      } else {
        toast.success("Lookup saved.");
      }
      setQueryName(""); setResult(null); setRisk(null);
      if (!compact) loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="enforcement-panel">
      {!compact && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "16px", marginBottom: "24px" }}>
          <StatCard label="Total Lookups" value={stats.total} color="#2563eb" icon={Gavel} />
          <StatCard label="ED Actions" value={stats.ed} color="#fca5a5" icon={ShieldAlert} />
          <StatCard label="CBI Actions" value={stats.cbi} color="#c4b5fd" icon={Users} />
          <StatCard label="SFIO Probes" value={stats.sfio} color="#fbbf24" icon={FlaskConical} />
          <StatCard label="Cases Created" value={stats.cases_created} color="#f59e0b" icon={FileText} />
        </div>
      )}

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: compact ? "16px" : "24px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles style={{ width: 18, height: 18, color: "#2563eb" }} />
          <h2 style={{ fontSize: compact ? "14px" : "16px", fontWeight: 700, margin: 0, color: "#f1f5f9" }}>
            {compact ? "Enforcement Quick Lookup" : "ED / CBI / SFIO Enforcement Search"}
          </h2>
        </div>
        <div className="flex gap-3">
          <input
            type="text" value={queryName} onChange={(e) => setQueryName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runLookup()}
            placeholder="Person or company name (e.g., 'Mehul Choksi', 'Amrapali Group')"
            data-testid="enforcement-query-input"
            style={{ flex: 1, background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "13px" }}
          />
          <button onClick={runLookup} disabled={looking} data-testid="enforcement-lookup-btn"
            style={{ background: looking ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              border: "none", borderRadius: "8px", color: "white", padding: "10px 22px",
              fontWeight: 600, fontSize: "13px", cursor: looking ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap" }}>
            {looking ? (<><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Searching...</>) : (<><Search style={{ width: 14, height: 14 }} /> Lookup Enforcement</>)}
          </button>
        </div>
        <div style={{ color: "#64748b", fontSize: "11px", marginTop: "8px" }}>
          ED · CBI · SFIO · Powered by Claude Sonnet 4.5
        </div>
      </div>

      {/* Result */}
      {result && (
        <>
        <div style={{ marginTop: "20px" }}>
          <IdentityResultCard person={fromEnforcement(result, risk)} />
        </div>
        <div
          style={{ background: "#0d1117", border: `1px solid ${risk?.severity_level === "critical" ? "#dc2626" : "#1e2530"}`, borderRadius: "12px", padding: "24px", marginTop: "20px" }}
          data-testid="enforcement-result-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Gavel style={{ color: "#2563eb", width: 22, height: 22 }} />
              <h3 style={{ color: "#f1f5f9", fontSize: "18px", fontWeight: 700, margin: 0 }}>{result.query_name}</h3>
              <span style={{ background: FLAG_COLORS[risk?.severity_level || "low"].bg, color: FLAG_COLORS[risk?.severity_level || "low"].text, border: `1px solid ${FLAG_COLORS[risk?.severity_level || "low"].border}`, borderRadius: "6px", padding: "4px 10px", fontSize: "11px", fontWeight: 700 }}>
                {(risk?.severity_level || "low").toUpperCase()}
              </span>
            </div>
            <button onClick={handleSave} disabled={saving} data-testid="save-enforcement-btn"
              style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: "6px", color: "white", padding: "8px 16px", fontWeight: 600, fontSize: "12px", cursor: saving ? "wait" : "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
              {saving ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <FileText style={{ width: 12, height: 12 }} />}
              {saving ? "Saving..." : "Save & Flag"}
            </button>
          </div>

          {/* 3 Agency cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "16px" }}>
            <AgencyCard
              agency="ED"
              data={result.ed_data}
              fields={[
                { key: "action_type", label: "Action" },
                { key: "persons", label: "Persons" },
                { key: "companies", label: "Companies" },
                { key: "amount_involved", label: "Amount", format: formatINR },
                { key: "watchlist_entries", label: "Entries" },
              ]}
            />
            <AgencyCard
              agency="CBI"
              data={result.cbi_data}
              fields={[
                { key: "action_type", label: "Action" },
                { key: "persons", label: "Persons" },
                { key: "banks_defrauded", label: "Banks" },
                { key: "total_fraud_amount", label: "Amount", format: formatINR },
                { key: "watchlist_entries", label: "Entries" },
              ]}
            />
            <AgencyCard
              agency="SFIO"
              data={result.sfio_data}
              fields={[
                { key: "companies_under_investigation", label: "Companies" },
                { key: "directors_accused", label: "Directors" },
                { key: "shell_companies_identified", label: "Shells", format: (v) => v },
                { key: "auditor_debarred", label: "Auditor Debarred", format: (v) => (v ? "Yes" : "No") },
                { key: "watchlist_entries", label: "Entries" },
              ]}
            />
          </div>

          {/* Risk flags */}
          {risk?.flags?.length > 0 && (
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                Enforcement Flags ({risk.flags.length}) · +{risk.risk_score_added} risk points
              </div>
              {risk.flags.map((f, idx) => {
                const c = FLAG_COLORS[f.severity] || FLAG_COLORS.medium;
                return (
                  <div key={idx} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "6px", padding: "8px 12px", marginBottom: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <AlertTriangle style={{ width: 14, height: 14, color: c.text }} />
                    <span style={{ color: c.text, fontSize: "12px", fontWeight: 600 }}>{f.label}</span>
                    <span style={{ color: c.text, fontSize: "9px", marginLeft: "auto", textTransform: "uppercase", letterSpacing: "1px" }}>{f.severity}</span>
                  </div>
                );
              })}
            </div>
          )}

          {risk?.flags?.length === 0 && (
            <div style={{ color: "#22c55e", fontSize: "13px", textAlign: "center", padding: "16px" }}>
              ✓ Clear — no enforcement actions on record across ED, CBI, SFIO.
            </div>
          )}

          <div style={{ marginTop: "12px", color: "#475569", fontSize: "10px", textAlign: "right" }}>
            AI: {result.ai_mode || "claude-sonnet-4.5"} · Source: {result.source}
          </div>
        </div>
        </>
      )}

      {!compact && (
        <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "24px", marginTop: "24px" }}>
          <h2 style={{ fontSize: "16px", fontWeight: 700, margin: "0 0 16px", color: "#f1f5f9" }}>Recent Lookups ({history.length})</h2>
          {history.length === 0 ? (
            <div style={{ color: "#64748b", fontSize: "13px", textAlign: "center", padding: "32px" }}>
              No lookups yet. Try "Mehul Choksi" — saare 3 agencies hit milengi.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "8px" }}>
              {history.map((l) => (
                <div key={l.id} style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr auto auto auto auto", alignItems: "center", gap: "12px" }}>
                  <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>{l.query_name}</div>
                  {l.risk?.has_ed && <span style={{ background: AGENCY_COLORS.ED.bg, color: AGENCY_COLORS.ED.text, borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontWeight: 700 }}>ED</span>}
                  {l.risk?.has_cbi && <span style={{ background: AGENCY_COLORS.CBI.bg, color: AGENCY_COLORS.CBI.text, borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontWeight: 700 }}>CBI</span>}
                  {l.risk?.has_sfio && <span style={{ background: AGENCY_COLORS.SFIO.bg, color: AGENCY_COLORS.SFIO.text, borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontWeight: 700 }}>SFIO</span>}
                  <div style={{ color: "#64748b", fontSize: "10px" }}>{new Date(l.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EnforcementPage() {
  return (
    <div data-testid="enforcement-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-2">
        <Gavel style={{ width: 26, height: 26, color: "#2563eb" }} />
        <h1 style={{ fontSize: "26px", fontWeight: 700, margin: 0, color: "#f1f5f9" }} data-testid="page-title">ED / CBI / SFIO Lookup</h1>
      </div>
      <p style={{ color: "#94a3b8", fontSize: "13px", marginBottom: "24px" }}>
        Indian Enforcement Agencies search — PMLA arrests, FEMA, attachments, CBI chargesheets, SFIO probes &amp; shell-co identification.
      </p>
      <EnforcementPanel />
    </div>
  );
}
